import { ComponentRef, Injectable, Injector, ViewContainerRef } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import {
	IFileTypeConfig,
	IFileUploadEvent,
	IRequestObject,
	IUploadOptions,
} from '../au-file-loader.interfaces';
import { Apollo } from 'apollo-angular';
import { AuFileLoaderCore } from '@shared/ui-components/au-file-loader/services/file-loader-core.service';
import {
	AuFileLoadFloatingWindowComponent,
	IFileEvent,
} from '@shared/ui-components/au-file-loader/au-file-load-floating-window/au-file-load-floating-window.component';
import { DeleteFileMutation } from '@shared/ui-components/au-file-loader/au-file-loader.queries';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { DocumentNode } from 'graphql';
// TODO we have such a map in the environment file, use it instead!
const endpointsMap: Record<string, string> = {
	cmms: environment.cmmsGraphql,
	global: environment.graphql,
	federation: environment.federationGraphql,
	digitalTwin: environment.digitalTwinGraphql,
};

export interface IFileLoaderOptions {
	restrictMultipleFilesLoading: boolean;
	instantlyOpenSelectionDialog: boolean;
	fileTypesConfig: IFileTypeConfig;
	mutation?: DocumentNode;
	mutationVariables?: { [key: string]: any };
	endpointName?: string;
	disableLocationPicker?: boolean;
}

@Injectable({ providedIn: 'root' })
export class FileLoaderService {
	componentRef: ComponentRef<AuFileLoadFloatingWindowComponent>;
	containerRef: ViewContainerRef;
	fileEventsSubscription: Subject<IFileEvent> = new Subject<IFileEvent>();

	constructor(
		private fileLoaderCore: AuFileLoaderCore,
		private apollo: Apollo,
		private notify: IconSnackBarService,
		private injector: Injector
	) {}

	upload(uploadOption: IUploadOptions): Observable<IFileUploadEvent> {
		const endpoint = uploadOption.endpointName
			? endpointsMap[uploadOption.endpointName]
			: endpointsMap['cmms'];

		const request: IRequestObject = {
			file: uploadOption.file,
			index: uploadOption.index,
			requestBrakeSubject$: uploadOption.requestBrakeSubject$,
			httpUri: endpoint,
			mutation: uploadOption.mutation || null,
			mutationVariables: uploadOption.mutationVariables || null,
		};

		return this.fileLoaderCore.makeRequest(request);
	}

	// TODO it means it will not delete files from other endpoints, right? Rewrite this!
	remove(ids: string[]) {
		return this.apollo.mutate<{ success: boolean }>({
			mutation: DeleteFileMutation,
			variables: { files: ids },
		});
	}

	createFileLoaderFloatingWindow(
		entry?: ViewContainerRef,
		options?: Partial<IFileLoaderOptions>,
		temporaryFileLoaderOptions?: {
			showFileTypesHint: boolean;
			disableBulkSelection: boolean;
		}
	): Observable<IFileEvent> {
		const container = entry ?? this.containerRef;
		if (!container) {
			throw new Error('Please provide entry component');
		}

		if (this.componentRef) {
			return this.fileEventsSubscription.asObservable();
		}

		import('../au-file-load-floating-window/au-file-load-floating-window.component').then(
			({ AuFileLoadFloatingWindowComponent }) => {
				this.componentRef = container.createComponent(AuFileLoadFloatingWindowComponent, {
					injector: this.injector,
				});

				this.componentRef.instance.options = options;
				this.componentRef.instance.temporaryOptions = temporaryFileLoaderOptions;

				const closeSubscription: Observable<void> = this.componentRef.instance.closeEvent;

				this.componentRef.instance.fileEventFinished.subscribe(event => {
					this.fileEventsSubscription.next(event);
				});

				closeSubscription.pipe(take(1)).subscribe(() => {
					this.destroyComponentAndSubscriptions();
				});
			}
		);

		return this.fileEventsSubscription.asObservable();
	}

	closeLoaderWindow() {
		this.destroyComponentAndSubscriptions();
	}

	saveFileOnUserComputer(url, name = 'untitled'): void {
		if (!url) {
			console.error('Resource URL not provided! You need to provide one');
		}
		fetch(url)
			.then(response => response.blob())
			.then(blob => {
				const blobURL = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = blobURL;
				a.download = name;
				document.body.appendChild(a);
				a.click();
				a.remove();
			})
			.catch(errorMsg => {
				this.notify.error(errorMsg || 'Downloading procedure failed');
			});
	}

	private destroyComponentAndSubscriptions() {
		if (this.componentRef?.destroy) {
			this.componentRef.destroy();
		}

		this.componentRef = null;
	}
}
